import React from "react"
import { type ApolloError } from "@apollo/client"
import { isNetworkRequestInFlight } from "@apollo/client/core/networkStatus"
import {
  DirectionFromOrigin,
  MissingObject,
  type TransactionSummary,
  useSummarizeTransactionsByTimeQuery,
} from "@digits-graphql/frontend/graphql-bearer"
import { molecule, use, useMolecule } from "bunshi/react"
import { atom, useAtomValue, useSetAtom } from "jotai"
import { LegalEntityIdScope } from "src/frontend/components/LegalEntityIdScope"
import {
  MISSING_PARTY_ENTITY,
  MISSING_PARTY_ID,
} from "src/frontend/components/OS/Applications/VendorDirectory/constants"
import { usePartyRoles } from "src/frontend/components/OS/Applications/VendorDirectory/hooks/usePartyRoles"
import { useVendorCustomerCustomization } from "src/frontend/components/OS/Applications/VendorDirectory/hooks/useVendorCustomerCustomization"
import { summaryFilterVariablesMolecule } from "src/frontend/components/OS/Applications/VendorDirectory/moleculeHydrators/filteredVendors/summaryFilterMolecule"
import { VendorDirectoryScope } from "src/frontend/components/OS/Applications/VendorDirectory/VendorScopeProvider"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import useIntervalOrigin from "src/shared/hooks/useIntervalOrigin"

/*
  MOLECULES
*/

export const filteredMissingPartyMolecule = molecule(() => {
  // Vendor directory scope does not provide a value that changes, so it just specifies the scope boundary for vendor directory data
  use(VendorDirectoryScope)
  use(LegalEntityIdScope)

  const rawMissingPartyDataAtom = atom<TimeSummary[] | undefined>(undefined)
  const totalCountAtom = atom(
    (get) =>
      get(rawMissingPartyDataAtom)?.reduce((acc, v) => {
        const count = v?.summary?.total.transactionsCount ?? 0
        return acc + count
      }, 0) ?? 0
  )

  const missingPartyDataAtom = atom((get) => {
    const value = get(rawMissingPartyDataAtom)
    const summary = value?.[0]?.summary
    const transactionCount = get(totalCountAtom)

    return value && summary && transactionCount > 0
      ? {
          summary: {
            ...summary,
            total: {
              ...summary.total,
              transactionsCount: transactionCount,
            },
          },
          partyObject: MISSING_PARTY_ENTITY,
        }
      : undefined
  })
  const loadingAtom = atom(false)
  const errorAtom = atom<ApolloError | undefined>(undefined)

  return {
    loadingAtom,
    errorAtom,
    rawMissingPartyDataAtom,
    missingPartyDataAtom,
  }
})

/*
  INTERFACES
*/

interface TimeSummary {
  summary: TransactionSummary
}

/*
  HYDRATORS
*/

// useHydrateFilteredMissingPartyMolecule hydrates the missing party molecule with the results of the summarize transactions by time query
export const useHydrateFilteredMissingPartyMolecule = () => {
  const intervalOrigin = useIntervalOrigin()
  const viewKey = useViewVersion()
  const partyRoles = usePartyRoles()
  const { defaultCategoryType } = useVendorCustomerCustomization()
  const { filterVariablesAtom } = useMolecule(summaryFilterVariablesMolecule)
  const filterVariables = useAtomValue(filterVariablesAtom)

  const { errorAtom, loadingAtom, rawMissingPartyDataAtom } = useMolecule(
    filteredMissingPartyMolecule
  )
  const setError = useSetAtom(errorAtom)
  const setLoading = useSetAtom(loadingAtom)
  const setMissingPartyData = useSetAtom(rawMissingPartyDataAtom)

  const { data, error, networkStatus } = useSummarizeTransactionsByTimeQuery({
    variables: {
      filter: {
        ...filterVariables,
        viewKey,
        partyRoles,
        missingParty: MissingObject.Include,
      },
      direction: DirectionFromOrigin.Past,
      origin: {
        ...intervalOrigin,
        intervalCount: intervalOrigin?.intervalCount || 1,
      },
      defaultCategoryType,
    },
    notifyOnNetworkStatusChange: true,
  })

  React.useEffect(() => {
    if (!isNetworkRequestInFlight(networkStatus)) {
      setMissingPartyData(data?.dimensionalTransactionSummary?.time)
    }
  }, [data, setMissingPartyData, networkStatus])

  React.useEffect(() => {
    if (error) {
      setError(error)
    }
  }, [error, setError])

  React.useEffect(() => {
    setLoading(isNetworkRequestInFlight(networkStatus))
  }, [networkStatus, setLoading])
}

/*
  HELPERS
*/

export const isMissingParty = (id: string) => id === MISSING_PARTY_ID
